import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CreditCard, ExternalLink, FileText, ShieldCheck, UserPlus, Users } from 'lucide-react';
import { useAuth } from '../../context/authcontext';
import { useCanPerform } from '../../hooks/useCanPerform';
import { SubscriptionService } from '../../services/billing/subscription.service';
import type {
  DowngradeOverCapDetails,
  InvoiceStatus,
  InvoicesResponse,
  PendingChargeDto,
  PlanChangePreviewDto,
  PlanDto,
  PlansResponse,
  SubscriptionDto,
  SubscriptionStatus,
} from '../../dtos/billing/subscription.dto';
import { describeApiError, getApiErrorCode } from '../../utils/apiErrors';
import { isApiError } from '../../dtos/common/api.dto';
import {
  BILLING_DISABLED_TITLE,
  capPercent,
  cardExpiresBefore,
  cardSummary,
  chargeReasonLabel,
  formatAccountDate,
  formatCents,
  formatCentsExact,
  formatShortDate,
  planAction,
  planActionReason,
  planLabel,
} from '../../utils/subscription';
import { openPatientChartById } from '../../constants/navigationEvents';
import { ConfirmDialog } from '../common';
import PlanCards, { planPrice } from '../subscription/PlanCards';
import { trialMessage } from '../subscription/SubscriptionBanner';
import AuditLogExportSection from './AuditLogExportSection';
import DataExportSection from './export/DataExportSection';
import SignedBaaSection from './SignedBaaSection';

export { BILLING_DISABLED_TITLE };

const STATUS_STYLES: Record<SubscriptionStatus, { label: string; container: string }> = {
  trialing: { label: 'Free trial', container: 'border-sky-200 bg-sky-50 text-sky-800' },
  active: { label: 'Account in good standing', container: 'border-green-200 bg-green-50 text-green-800' },
  past_due: { label: 'Payment failed', container: 'border-amber-200 bg-amber-50 text-amber-800' },
  read_only: { label: 'Account is read-only', container: 'border-red-200 bg-red-50 text-red-800' },
  suspended: { label: 'Account suspended', container: 'border-red-200 bg-red-50 text-red-800' },
  canceled: { label: 'Subscription canceled', container: 'border-slate-300 bg-slate-100 text-slate-800' },
};

const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  open: 'Due',
  paid: 'Paid',
  uncollectible: 'Uncollectible',
  void: 'Void',
};

const CHARGE_STATUS_LABELS: Record<PendingChargeDto['status'], string> = {
  pending: 'On next invoice',
  invoiced: 'Invoiced',
  void: 'Void',
};

/** Normalize 409 DOWNGRADE_OVER_CAP details; null when the shape is unusable. */
function overCapDetails(err: unknown): DowngradeOverCapDetails | null {
  if (!isApiError(err) || err.domainCode !== 'DOWNGRADE_OVER_CAP' || !err.details) return null;
  const { used, limit, patients } = err.details as Record<string, unknown>;
  const list = Array.isArray(patients)
    ? patients
        .filter((p): p is { patientId: string; name?: unknown } => !!p && typeof (p as { patientId?: unknown }).patientId === 'string')
        .map((p) => ({ patientId: p.patientId, name: typeof p.name === 'string' && p.name ? p.name : p.patientId }))
    : [];
  return { used: typeof used === 'number' ? used : list.length, limit: typeof limit === 'number' ? limit : 0, patients: list };
}

/**
 * Organization Account (Phase 13, §4.2; plan switching §4.2): the real plan, status, dates, patient
 * cap usage and active users; plan catalog from GET /plans; for managers (admin:subscription:manage)
 * a previewed plan switch (immediate during the trial; paid plans upgrade now and downgrade at the
 * period end, with "Keep" to undo), the card on file (add / replace through Checkout in setup mode),
 * billing details and invoices in the customer portal, and cancel; billing history from the invoice
 * mirror; signed BAA and the HIPAA audit log export. The page itself is gated by read:config in AdminHome.
 */
const OrganizationAccount: React.FC = () => {
  const { user, refreshPermissions } = useAuth();
  const canManage = useCanPerform('manage:subscription');
  const timeZone = user?.agencyTimezone;

  const [catalog, setCatalog] = useState<PlansResponse | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionDto | null>(null);
  const [history, setHistory] = useState<InvoicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [catalogError, setCatalogError] = useState('');
  const [historyError, setHistoryError] = useState('');

  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [changeTarget, setChangeTarget] = useState<PlanDto | null>(null);
  const [preview, setPreview] = useState<PlanChangePreviewDto | null>(null);
  const [previewState, setPreviewState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const previewRequest = useRef(0);
  const [overCap, setOverCap] = useState<DowngradeOverCapDetails | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [existingSampleId, setExistingSampleId] = useState<string | null>(null);

  const load = useCallback(async (isCancelled: () => boolean = () => false) => {
    setLoading(true);
    setLoadError('');
    const [plansResult, subscriptionResult, historyResult] = await Promise.allSettled([
      SubscriptionService.getPlans(),
      SubscriptionService.getSubscription(),
      SubscriptionService.getInvoices(),
    ]);
    if (isCancelled()) return;
    if (plansResult.status === 'fulfilled') {
      setCatalog(plansResult.value);
      setCatalogError('');
    } else {
      setCatalogError(describeApiError(plansResult.reason, 'Unable to load plans.'));
    }
    if (subscriptionResult.status === 'fulfilled') {
      setSubscription(subscriptionResult.value);
    } else {
      setLoadError(describeApiError(subscriptionResult.reason, 'Unable to load your subscription.'));
    }
    if (historyResult.status === 'fulfilled') {
      setHistory(historyResult.value);
      setHistoryError('');
    } else {
      setHistoryError(describeApiError(historyResult.reason, 'Unable to load billing history.'));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  /** Show the new subscription and refresh the banners (they read /users/me/permissions). */
  const applySubscription = (next: SubscriptionDto) => {
    setSubscription(next);
    void refreshPermissions?.();
  };

  const startAction = (key: string) => {
    setBusy(key);
    setActionError('');
    setNotice('');
  };

  const redirectAction = async (key: string, getUrl: () => Promise<string>, fallback: string) => {
    startAction(key);
    try {
      SubscriptionService.redirectTo(await getUrl());
    } catch (err) {
      setActionError(describeApiError(err, fallback));
      setBusy(null);
    }
  };

  /** Open the confirm dialog and load the price preview (S4); a failed preview falls back to static text. */
  const openChangeDialog = (plan: PlanDto) => {
    const request = previewRequest.current + 1;
    previewRequest.current = request;
    setChangeTarget(plan);
    setPreview(null);
    setPreviewState('loading');
    SubscriptionService.previewChange(plan.id)
      .then((result) => {
        if (previewRequest.current !== request) return;
        setPreview(result);
        setPreviewState('ready');
      })
      .catch(() => {
        if (previewRequest.current === request) setPreviewState('error');
      });
  };

  const closeChangeDialog = () => {
    previewRequest.current += 1;
    setChangeTarget(null);
    setPreview(null);
    setPreviewState('idle');
  };

  const choosePlan = (plan: PlanDto) => {
    if (!subscription) return;
    setOverCap(null);
    const action = planAction(subscription.status, subscription.hasPaymentMethod === true, subscription.cancelAtPeriodEnd);
    if (action === 'checkout') {
      void redirectAction(`plan:${plan.id}`, () => SubscriptionService.checkout(plan.id), 'Unable to start checkout.');
    } else if (action === 'change') {
      openChangeDialog(plan);
    }
  };

  const confirmChange = async () => {
    if (!changeTarget) return;
    const target = changeTarget;
    const wasTrial = subscription?.status === 'trialing';
    startAction(`plan:${target.id}`);
    try {
      const next = await SubscriptionService.changePlan(target.id);
      applySubscription(next);
      const effective = formatAccountDate(next.pendingPlanAt ?? next.currentPeriodEnd, timeZone);
      // An upgrade waiting on 3-D Secure or declined keeps the old plan: the notices in the Plans block explain it
      if (next.plan === target.id) {
        setNotice(wasTrial ? `Your trial is now on ${target.name}.` : `Your plan is now ${target.name}.`);
      } else if (next.pendingPlan === target.id && !next.pendingAction) {
        setNotice(`Your plan changes to ${target.name}${effective ? ` on ${effective}` : ' at the end of this period'}.`);
      }
    } catch (err) {
      const details = overCapDetails(err);
      if (details) setOverCap(details);
      else setActionError(describeApiError(err, 'Unable to change your plan.'));
    } finally {
      closeChangeDialog();
      setBusy(null);
    }
  };

  const undoChange = async () => {
    startAction('keep');
    try {
      const next = await SubscriptionService.undoChange();
      applySubscription(next);
      setNotice(`You will stay on ${planLabel(next.plan)}.`);
    } catch (err) {
      setActionError(describeApiError(err, 'Unable to keep your current plan.'));
    } finally {
      setBusy(null);
    }
  };

  const confirmCancel = async () => {
    startAction('cancel');
    try {
      applySubscription(await SubscriptionService.cancel());
      setNotice('Your subscription is canceled. Access continues until the end of the current period.');
    } catch (err) {
      setActionError(describeApiError(err, 'Unable to cancel your subscription.'));
    } finally {
      setCancelOpen(false);
      setBusy(null);
    }
  };

  const undoCancel = async () => {
    startAction('undo');
    try {
      applySubscription(await SubscriptionService.undoCancel());
      setNotice('Your subscription will continue.');
    } catch (err) {
      setActionError(describeApiError(err, 'Unable to undo the cancellation.'));
    } finally {
      setBusy(null);
    }
  };

  const loadSamplePatient = async () => {
    startAction('sample');
    setExistingSampleId(null);
    try {
      const patientId = await SubscriptionService.loadSamplePatient();
      openPatientChartById(patientId);
    } catch (err) {
      if (getApiErrorCode(err) === 'SAMPLE_EXISTS' && isApiError(err) && typeof err.details?.patientId === 'string') {
        setExistingSampleId(err.details.patientId);
      }
      setActionError(describeApiError(err, 'Unable to load the sample patient.'));
    } finally {
      setBusy(null);
    }
  };

  const plans = catalog?.plans ?? [];
  const currentPlan = subscription ? plans.find((plan) => plan.id === subscription.plan) : undefined;
  const status = subscription?.status ?? null;
  const billingEnabled = subscription?.billingEnabled === true;
  const endDate = formatAccountDate(subscription?.currentPeriodEnd ?? subscription?.trialEndsAt ?? null, timeZone);
  const canCancel = !!subscription && !subscription.cancelAtPeriodEnd && (status === 'trialing' || status === 'active' || status === 'past_due');
  const hasCard = subscription?.hasPaymentMethod === true;
  const card = subscription?.paymentMethod ?? null;
  const trialing = status === 'trialing';
  const trialEnd = formatAccountDate(subscription?.trialEndsAt ?? null, timeZone);
  const canOpenPortal = hasCard && status !== 'canceled';
  // Card capture is Checkout in setup mode: replace a card in any open state (it also pays an open
  // invoice); add the first one during the trial. After the trial, a plan card's Checkout takes the card.
  const canReplaceCard = hasCard && status !== 'canceled';
  const canAddCard = trialing && !hasCard && !!subscription && !subscription.cancelAtPeriodEnd;
  const renewalIso = trialing ? subscription?.trialEndsAt ?? null : subscription?.currentPeriodEnd ?? null;
  const cardExpiring = !!card && status !== 'canceled' && cardExpiresBefore(card, renewalIso);
  const noPlanReason = subscription ? planActionReason(subscription.status, hasCard, subscription.cancelAtPeriodEnd) : null;
  const pendingDowngrade = subscription?.pendingPlan && subscription.pendingPlan !== subscription.plan && !subscription.pendingAction
    ? subscription.pendingPlan
    : null;
  const pendingDate = formatAccountDate(subscription?.pendingPlanAt ?? subscription?.currentPeriodEnd ?? null, timeZone);
  const currentName = currentPlan?.name ?? planLabel(subscription?.plan);
  const upgradeFailed = subscription?.upgradeFailed && subscription.upgradeFailed.plan !== subscription.plan ? subscription.upgradeFailed : null;

  return (
    <div className="space-y-4 pb-6">
      <header className="bg-white rounded-xl border border-slate-200 px-4 py-4 flex flex-col gap-3">
        {subscription && (
          <div className="flex items-center justify-start gap-2">
            <div data-testid="account-standing" className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium ${STATUS_STYLES[subscription.status].container}`}>
              <ShieldCheck size={17} aria-hidden="true" /> {STATUS_STYLES[subscription.status].label}
            </div>
          </div>
        )}
        <div className="text-center"><h1 className="text-xl font-semibold text-slate-900">Organization Account</h1><p className="text-sm text-slate-500 mt-1">Manage your organization&apos;s plan, billing, and account health.</p></div>
      </header>

      <SignedBaaSection />

      {loading && !subscription && <p className="text-sm text-slate-500" role="status">Loading your subscription...</p>}
      {loadError && <p className="text-sm text-red-700" role="alert">{loadError}</p>}

      {subscription && (
        <section aria-labelledby="account-overview-heading">
          <h2 id="account-overview-heading" className="text-base font-semibold text-slate-900 mb-2">Overview</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            <OverviewCard title="Plan" icon={<CreditCard size={16} />}>
              <p className="font-semibold text-slate-900" data-testid="current-plan-name">{currentPlan?.name ?? planLabel(subscription.plan)}</p>
              {currentPlan && <p className="text-base sm:text-lg font-semibold text-slate-900 mt-1">{planPrice(currentPlan)}</p>}
              {currentPlan && currentPlan.priceCents !== null && <p className="text-[11px] sm:text-xs text-slate-500">plus applicable tax</p>}
              <p className="text-xs text-slate-600 mt-2" data-testid="plan-period-line">{periodLine(subscription, timeZone)}</p>
              {pendingDowngrade && (
                <p className="text-xs text-slate-600 mt-1">Changes to {planLabel(pendingDowngrade)}{pendingDate ? ` on ${pendingDate}` : ' at the end of this period'}.</p>
              )}
            </OverviewCard>
            <OverviewCard title="Patients" icon={<FileText size={16} />}>
              <CapUsage used={subscription.cap.used} limit={subscription.cap.limit} />
              <p className="text-[11px] text-slate-500 mt-2">Active and on-hold patients count. Referrals, discharged and sample patients do not.</p>
            </OverviewCard>
            <OverviewCard title="Team" icon={<Users size={16} />}>
              <p className="text-xl sm:text-2xl font-semibold text-slate-900" data-testid="active-users">{subscription.users?.active ?? 0}</p>
              <p className="text-[11px] sm:text-xs text-slate-500 mt-1">Active users · unlimited on every plan</p>
            </OverviewCard>
          </div>
        </section>
      )}

      {subscription && (
        <section aria-labelledby="billing-heading" className="bg-white rounded-xl border border-slate-200">
          <div className="px-3 sm:px-4 py-3 sm:py-4 border-b border-slate-200">
            <h2 id="billing-heading" className="text-base font-semibold text-slate-900">Subscription &amp; Billing</h2>
            <p className="text-sm text-slate-500 mt-1">Monthly plans. Payment details are handled by our payment provider and are never stored by Pelora.</p>
            {!billingEnabled && <p className="text-xs text-slate-500 mt-1">{BILLING_DISABLED_TITLE}: payment actions are turned off for this account. Charges are recorded and shown below.</p>}
          </div>
          <div className="p-3 sm:p-4 space-y-4">
            {notice && <p className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800" role="status">{notice}</p>}
            {actionError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                <p>{actionError}</p>
                {existingSampleId && (
                  <button type="button" onClick={() => openPatientChartById(existingSampleId)} className="mt-1 text-sm font-medium underline">Open the sample patient</button>
                )}
              </div>
            )}
            {overCap && <DowngradeOverCapPanel details={overCap} onClose={() => setOverCap(null)} />}

            <div data-testid="payment-method">
              <h3 className="text-sm font-semibold text-slate-900">Payment method</h3>
              <p className="text-sm font-medium text-slate-800 mt-1" data-testid="card-summary">
                {card ? cardSummary(card) : hasCard ? 'Card on file' : 'No card yet'}
              </p>
              <p className="text-sm text-slate-600 mt-1">{paymentLine(subscription, hasCard, trialEnd)}</p>
              {cardExpiring && (
                <p className="mt-1 text-sm text-amber-800" role="alert" data-testid="card-expiry-warning">
                  This card expires before {formatAccountDate(renewalIso, timeZone)}. Replace it to avoid a failed payment.
                </p>
              )}
              {canManage && (canAddCard || canReplaceCard || canOpenPortal) && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {(canAddCard || canReplaceCard) && (
                    <button
                      type="button"
                      disabled={busy !== null || !billingEnabled}
                      title={billingEnabled ? undefined : BILLING_DISABLED_TITLE}
                      onClick={() => redirectAction('card', () => SubscriptionService.addPaymentMethod(), 'Unable to open the card page.')}
                      className="inline-flex items-center gap-2 rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <CreditCard size={16} aria-hidden="true" />
                      {busy === 'card' ? 'Opening...' : hasCard ? 'Replace card' : 'Add card'}
                    </button>
                  )}
                  {canOpenPortal && (
                    <button
                      type="button"
                      disabled={busy !== null || !billingEnabled}
                      title={billingEnabled ? undefined : BILLING_DISABLED_TITLE}
                      onClick={() => redirectAction('portal', () => SubscriptionService.portal(), 'Unable to open the billing page.')}
                      className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FileText size={16} aria-hidden="true" />
                      {busy === 'portal' ? 'Opening...' : 'Billing details and invoices'}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 pt-4">
              <h3 className="text-sm font-semibold text-slate-900">Plans</h3>
              <p className="text-xs text-slate-500 mb-3">{catalog?.taxNote || 'Monthly prices, plus applicable tax.'}</p>
              {subscription.pendingAction && (
                <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid="pending-action">
                  <p>Your bank needs you to confirm the payment for the upgrade.</p>
                  {subscription.pendingAction.invoiceUrl && (
                    <a
                      href={subscription.pendingAction.invoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex items-center gap-1 font-medium underline underline-offset-2"
                    >
                      Complete payment<ExternalLink size={12} aria-hidden="true" />
                    </a>
                  )}
                </div>
              )}
              {upgradeFailed && (
                <p className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid="upgrade-failed">
                  Your card was declined, so you are still on {currentName}.
                </p>
              )}
              {pendingDowngrade && (
                <p className="mb-3 text-sm text-slate-700" data-testid="pending-downgrade">
                  Changes to {planLabel(pendingDowngrade)}{pendingDate ? ` on ${pendingDate}` : ' at the end of this period'}.{' '}
                  {canManage && (
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={undoChange}
                      className="font-semibold text-slate-900 underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {busy === 'keep' ? 'Saving...' : `Keep ${currentName}`}
                    </button>
                  )}
                </p>
              )}
              {canManage && noPlanReason && (
                <p className="mb-3 text-sm text-slate-700" data-testid="plan-action-reason">{noPlanReason}</p>
              )}
              {catalogError ? (
                <p className="text-sm text-red-700" role="alert">{catalogError}</p>
              ) : (
                <PlanCards
                  plans={plans}
                  currentPlan={subscription.plan}
                  status={subscription.status}
                  billingEnabled={billingEnabled}
                  hasPaymentMethod={hasCard}
                  cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
                  canManage={canManage}
                  busyPlan={busy?.startsWith('plan:') ? (busy.slice(5) as PlanDto['id']) : null}
                  onChoose={choosePlan}
                />
              )}
            </div>

            {canManage && (canCancel || subscription.cancelAtPeriodEnd) && (
              <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
                {canCancel && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => setCancelOpen(true)}
                    className="rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancel subscription
                  </button>
                )}
                {subscription.cancelAtPeriodEnd && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={undoCancel}
                    className="rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busy === 'undo' ? 'Saving...' : 'Undo cancel'}
                  </button>
                )}
              </div>
            )}

            <BillingHistory history={history} error={historyError} timeZone={timeZone} />
          </div>
        </section>
      )}

      {canManage && (
        <section aria-labelledby="sample-patient-heading" className="bg-white rounded-xl border border-slate-200">
          <div className="px-3 sm:px-4 py-3 sm:py-4">
            <h2 id="sample-patient-heading" className="text-base font-semibold text-slate-900">Explore with a sample patient</h2>
            <p className="text-sm text-slate-500 mt-1">Adds a practice patient with made-up data (not real PHI). It does not count toward your patient limit, and you can delete it at any time.</p>
            <button
              type="button"
              disabled={busy !== null}
              onClick={loadSamplePatient}
              className="mt-3 inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <UserPlus size={16} aria-hidden="true" /> {busy === 'sample' ? 'Loading...' : 'Load sample patient'}
            </button>
          </div>
        </section>
      )}

      <DataExportSection />
      <AuditLogExportSection />

      <ConfirmDialog
        open={changeTarget !== null}
        title={`Change to ${changeTarget?.name ?? ''}?`}
        message={
          <div className="space-y-2">
            <p>Your organization will switch from {currentName} to {changeTarget?.name}{changeTarget ? ` (${planPrice(changeTarget)}, plus applicable tax)` : ''}.</p>
            {previewState === 'loading' ? (
              <p role="status">Calculating…</p>
            ) : preview && changeTarget ? (
              <p data-testid="plan-change-preview">{previewText(preview, changeTarget, currentName, hasCard, subscription?.trialEndsAt ?? null, timeZone)}</p>
            ) : trialing ? (
              <p>
                The change applies now and your free trial continues{trialEnd ? ` until ${trialEnd}` : ''}.{' '}
                {hasCard ? 'Your card is charged the new price when the trial ends.' : 'Nothing is charged until you add a card.'}
              </p>
            ) : (
              <p>An upgrade takes effect now, with a prorated charge on your next invoice. A downgrade takes effect at the end of the current billing period.</p>
            )}
          </div>
        }
        confirmLabel="Confirm plan change"
        busy={busy?.startsWith('plan:') ?? false}
        onConfirm={confirmChange}
        onCancel={closeChangeDialog}
      />

      <ConfirmDialog
        open={cancelOpen}
        title="Cancel subscription?"
        message={`Access continues until ${endDate ?? 'the end of the current period'}. No refund. Pending charges are billed on your final invoice.`}
        confirmLabel="Cancel subscription"
        cancelLabel="Keep subscription"
        destructive
        busy={busy === 'cancel'}
        onConfirm={confirmCancel}
        onCancel={() => setCancelOpen(false)}
      />
    </div>
  );
};

/** What the Payment method block says under the card summary for each state. */
function paymentLine(subscription: SubscriptionDto, hasCard: boolean, trialEnd: string | null): string {
  if (subscription.status === 'canceled') return 'Choose a plan below to subscribe again.';
  if (hasCard) {
    if (subscription.status === 'trialing') {
      return `The first charge is on ${trialEnd ?? 'the day your trial ends'}.`;
    }
    if (subscription.status === 'read_only' || subscription.status === 'suspended' || subscription.status === 'past_due') {
      return 'Your last payment failed. Replace the card to pay the open invoice and restore full access.';
    }
    return 'Your card is charged each month. Card numbers are kept by our payment provider, never by Pelora.';
  }
  if (subscription.status === 'trialing') {
    return `Add a card any time during the trial to keep access after ${trialEnd ?? 'it ends'}; you are not charged until then.`;
  }
  return 'Choose a plan below to add a card.';
}

/** The confirm dialog's price line from GET /subscription/change/preview (S4). */
function previewText(
  preview: PlanChangePreviewDto,
  target: PlanDto,
  currentName: string,
  hasCard: boolean,
  trialEndsAt: string | null,
  timeZone?: string
): string {
  const monthly = `${formatCents(target.priceCents)} / month`;
  if (preview.effective === 'trial') {
    if (!hasCard) return 'No charge now. Nothing is charged until you add a card.';
    const firstCharge = formatCentsExact(preview.nextInvoiceCents ?? target.priceCents);
    const date = formatAccountDate(preview.nextInvoiceAt ?? trialEndsAt, timeZone);
    return `No charge now. Your first charge of ${firstCharge} is on ${date ?? 'the day your trial ends'}.`;
  }
  if (preview.effective === 'now') {
    return `${formatCentsExact(preview.dueNowCents)} now (prorated, including ${formatCentsExact(preview.taxCents)} tax), then ${monthly}.`;
  }
  const date = formatAccountDate(preview.nextInvoiceAt, timeZone);
  return `You stay on ${currentName} until ${date ?? 'the end of this billing period'}, then ${monthly}.`;
}

/** The plan card's date line for each status. */
function periodLine(subscription: SubscriptionDto, timeZone?: string): string {
  const trialEnd = formatAccountDate(subscription.trialEndsAt, timeZone);
  const periodEnd = formatAccountDate(subscription.currentPeriodEnd, timeZone);
  if (subscription.cancelAtPeriodEnd) {
    const end = periodEnd ?? trialEnd;
    return end ? `Ends ${end}` : 'Ends at the end of this period';
  }
  switch (subscription.status) {
    case 'trialing':
      return `${trialMessage(subscription.trialDaysLeft)}${trialEnd ? ` Trial ends ${trialEnd}.` : ''}`;
    case 'active':
      return periodEnd ? `Renews ${periodEnd}` : 'Renews monthly';
    case 'past_due':
      return 'Payment failed. Replace your card to keep full access.';
    case 'read_only':
      return 'Read-only. Choose a plan to make changes again.';
    case 'suspended':
      return 'Suspended. Choose a plan to restore access.';
    case 'canceled': {
      const exportUntil = formatAccountDate(subscription.exportUntil, timeZone);
      return exportUntil ? `Canceled. Data export is available until ${exportUntil}.` : 'Canceled.';
    }
    default:
      return '';
  }
}

const OverviewCard: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
    <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-700 mb-2 sm:mb-3">{icon}{title}</div>
    {children}
  </div>
);

const CapUsage: React.FC<{ used: number; limit: number | null }> = ({ used, limit }) => {
  const pct = capPercent(used, limit);
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-600">
        <span>Patients</span>
        <span data-testid="cap-usage">{limit === null ? `${used} (no fixed limit)` : `${used} / ${limit}`}</span>
      </div>
      {pct !== null && (
        <div className="h-1.5 bg-slate-100 rounded-full mt-1" aria-hidden="true">
          <div className={`h-full rounded-full ${pct >= 100 ? 'bg-amber-600' : pct >= 80 ? 'bg-amber-400' : 'bg-slate-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
      )}
    </div>
  );
};

const DowngradeOverCapPanel: React.FC<{ details: DowngradeOverCapDetails; onClose: () => void }> = ({ details, onClose }) => (
  <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900" role="alert" data-testid="downgrade-over-cap">
    <p className="font-semibold">You have {details.used} active patients; that plan allows {details.limit}.</p>
    <p className="mt-1">Discharge {Math.max(details.used - details.limit, 0)} or more before downgrading, or keep your current plan.</p>
    {details.patients.length > 0 && (
      <ul className="mt-2 max-h-48 list-disc overflow-y-auto pl-5" aria-label="Patients counted toward the limit">
        {details.patients.map((patient) => <li key={patient.patientId}>{patient.name}</li>)}
      </ul>
    )}
    <button type="button" onClick={onClose} className="mt-2 text-sm font-medium underline">Dismiss</button>
  </div>
);

const BillingHistory: React.FC<{ history: InvoicesResponse | null; error: string; timeZone?: string }> = ({ history, error, timeZone }) => (
  <div className="border-t border-slate-200 pt-4">
    <h3 className="text-sm font-semibold text-slate-900 mb-3">Billing history</h3>
    {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
    {history && (
      <>
        {history.invoices.length === 0 ? (
          <p className="text-sm text-slate-500">No invoices yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm text-left" aria-label="Invoices">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr><th className="py-2 pr-4 font-medium">Invoice</th><th className="py-2 pr-4 font-medium">Period</th><th className="py-2 pr-4 font-medium">Total</th><th className="py-2 pr-4 font-medium">Tax</th><th className="py-2 pr-4 font-medium">Status</th><th className="py-2 font-medium">Links</th></tr>
              </thead>
              <tbody>
                {history.invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-slate-100 last:border-0" data-testid="invoice-row">
                    <td className="py-2 pr-4 text-slate-700">{invoice.number || '—'}</td>
                    <td className="py-2 pr-4 text-slate-700">{formatShortDate(invoice.periodStart, timeZone)} – {formatShortDate(invoice.periodEnd, timeZone)}</td>
                    <td className="py-2 pr-4 font-medium text-slate-900">{formatCentsExact(invoice.totalCents)}</td>
                    <td className="py-2 pr-4 text-slate-700">{formatCentsExact(invoice.taxCents)}</td>
                    <td className="py-2 pr-4 text-slate-700">{INVOICE_STATUS_LABELS[invoice.status] ?? invoice.status}</td>
                    <td className="py-2">
                      <div className="flex gap-3">
                        {invoice.hostedInvoiceUrl && <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-slate-700 underline underline-offset-2">View<ExternalLink size={12} aria-hidden="true" /></a>}
                        {invoice.invoicePdfUrl && <a href={invoice.invoicePdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-slate-700 underline underline-offset-2">PDF<ExternalLink size={12} aria-hidden="true" /></a>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <h4 className="text-sm font-semibold text-slate-900 mt-4 mb-2">Pending charges</h4>
        {history.pendingCharges.length === 0 ? (
          <p className="text-sm text-slate-500">No pending charges.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm text-left" aria-label="Pending charges">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr><th className="py-2 pr-4 font-medium">Date</th><th className="py-2 pr-4 font-medium">Description</th><th className="py-2 pr-4 font-medium">Amount</th><th className="py-2 font-medium">Status</th></tr>
              </thead>
              <tbody>
                {history.pendingCharges.map((charge) => (
                  <tr key={charge.id} className="border-b border-slate-100 last:border-0" data-testid="pending-charge-row">
                    <td className="py-2 pr-4 text-slate-700">{formatShortDate(charge.createdAt, timeZone)}</td>
                    <td className="py-2 pr-4 text-slate-700">{chargeReasonLabel(charge.reason)}</td>
                    <td className="py-2 pr-4 font-medium text-slate-900">{formatCentsExact(charge.amountCents)}</td>
                    <td className="py-2 text-slate-700">{CHARGE_STATUS_LABELS[charge.status] ?? charge.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )}
  </div>
);

export default OrganizationAccount;
